import type { GhmSignalKind } from "@ghm/mqtt-contract";
import type { LatestPort } from "@/features/devices/types";
import type { Machine } from "@/features/machines/types";
import { parseTag } from "@/features/machines/addressing";
import type { SignalConfig } from "@/features/signals/types";
import { resolveChartConfig } from "./chartWidgets";
import { resolveWidgetForSignalConfig } from "./resolveWidget";
import { resolveWidgetValue, type SlotRuntimeBySlot } from "./selectors";
import { widgetSources, type DashboardWidget } from "./types";

export interface CurrentReadingProof { context: object; ready: boolean }

/** Evidencia conservadora de UM readout real; nao certifica historicos ou o painel inteiro. */
export function hasCurrentReading({ widgets, machine, latestPorts, declaredSources,
  configHashes, signalConfigs, sourceKinds, slotRuntime, now }: {
  widgets: DashboardWidget[];
  machine: Machine;
  latestPorts: LatestPort[];
  declaredSources: ReadonlySet<string>;
  configHashes?: ReadonlyMap<string, string | null>;
  signalConfigs: ReadonlyMap<string, SignalConfig>;
  sourceKinds: Record<string, GhmSignalKind>;
  slotRuntime?: SlotRuntimeBySlot;
  now: Date;
}): boolean {
  const matchingPort = (source: string) => {
    const address = parseTag(source);
    if (!address || !declaredSources.has(source)) return undefined;
    const slot = machine.slots.find((item) => item.slotIndex === address.slotIndex);
    if (!slot) return undefined;
    const hash = configHashes?.get(slot.deviceId);
    const port = latestPorts.find((item) => item.deviceId === slot.deviceId && item.portIndex === address.portIndex);
    return hash && port?.configHashHex === hash ? port : undefined;
  };
  return widgets.some((widget) => {
    const resolved = resolveWidgetForSignalConfig(widget, signalConfigs.get(widget.source));
    const kind = sourceKinds[widget.source] ?? resolved.settings.signalKind;
    const chart = widget.widgetType === "line-chart" ? resolveChartConfig(resolved) : null;
    const currentChart = chart?.kind === "line" && chart.series === "rate" &&
      (kind === undefined || kind === "rate");
    const direct = ["number", "gauge", "progress", "tank", "multi-value"].includes(widget.widgetType);
    if (!direct && !currentChart) return false;
    if (["gauge", "progress", "tank"].includes(widget.widgetType)) {
      const min = resolved.settings.min ?? 0;
      const max = resolved.settings.max ?? 100;
      if (!Number.isFinite(min) || !Number.isFinite(max) || max <= min) return false;
    }
    // A ancora precisa existir: renderCard retorna null sem ela, mesmo que
    // alguma extraSource pudesse resolver sozinha.
    if (!matchingPort(widget.source) || !resolveWidgetValue(widget, machine, latestPorts, [], slotRuntime, now)) return false;
    const sources = widget.widgetType === "multi-value" ? widgetSources(widget) : [widget.source];
    return sources.some((source) => {
      const port = matchingPort(source);
      if (!port) return false;
      const measuredAt = Date.parse(port.measuredAt ?? "");
      if (!Number.isFinite(measuredAt) || measuredAt > now.getTime()) return false;
      const value = resolveWidgetValue({ ...widget, source }, machine, latestPorts, [], slotRuntime, now);
      return value !== null && value.isAvailable && value.quality === "live" &&
        (value.diagnostic === undefined || value.diagnostic === "normal") &&
        value.numericValue !== null && Number.isFinite(value.numericValue);
    });
  });
}
